import { createRootRouteWithContext, Outlet, useNavigate } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';
import { Toast } from '@/components/ui/Toast';
import { GrainOverlay } from '@/components/motifs/GrainOverlay';
import { UnlockListener } from '@/components/unlock/UnlockListener';
import { HelpOverlay } from '@/components/help/HelpOverlay';
import { useKeyboardShortcuts } from '@/lib/keyboard';

interface RouterContext { queryClient: QueryClient }

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
});

function Root() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session === null) void navigate({ to: '/login' });
  }, [session, navigate]);

  if (session === undefined) return <SplashScreen />;

  return <Shell signedIn={!!session} />;
}

/**
 * App shell. The keyboard hook lives here so it's mounted on every route,
 * but its actions are no-ops when there's no signed-in session (no UI
 * surfaces to manipulate). UnlockListener is gated on session because it
 * subscribes to authenticated realtime channels.
 */
function Shell({ signedIn }: { signedIn: boolean }) {
  useKeyboardShortcuts();
  return (
    <>
      <GrainOverlay />
      <Outlet />
      {signedIn && <UnlockListener />}
      <HelpOverlay />
      <Toast />
    </>
  );
}

function SplashScreen() {
  return (
    <div style={{
      display: 'grid', placeItems: 'center', height: '100dvh',
      fontFamily: 'var(--font-display)', fontSize: 'var(--t-display)',
      color: 'var(--c-rose-deep)', letterSpacing: '-0.02em',
    }}>
      <div className="a-breathe">
        <span style={{ fontStyle: 'italic' }}>memory</span>
        <span style={{ color: 'var(--c-mauve)' }}> · </span>
        <span>map</span>
      </div>
    </div>
  );
}
